import { Check, Loader2 } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { cn } from "@/lib/utils";

/** Нижняя шторка: полупрозрачный скрим + панель со скруглением сверху.
 *  Тап по скриму закрывает; клик внутри панели — нет. Аппаратная «назад»
 *  тоже закрывает шторку, а не уводит с экрана скана (см. useBackGuard).
 *  Общий примитив скан-экранов (order-check / transfer-check / returns). */
export function BottomSheet({
  onClose,
  children,
  panelClassName,
}: {
  onClose: () => void;
  children: React.ReactNode;
  panelClassName?: string;
}): React.ReactElement {
  useBackGuard(true, onClose);
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-slate-900/45" onClick={onClose}>
      <div className={cn("rounded-t-2xl bg-white", panelClassName)} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/** Центрированный диалог подтверждения (2 кнопки).
 *  Аппаратная «назад» = «Отмена»: диалог создаётся перед необратимым действием,
 *  и уводить с экрана, не спросив, нельзя. */
export function Dialog({
  title,
  body,
  confirmLabel,
  confirmBusy,
  confirmDisabled,
  onConfirm,
  onCancel,
  danger,
  secondaryLabel,
  onSecondary,
}: {
  title: string;
  /** Текст или разметка: подтверждение завершения нередко должно показать
   *  список того, что уедет в документ, а строкой это не выразить. */
  body: React.ReactNode;
  confirmLabel: string;
  confirmBusy?: boolean;
  /** Подтверждать пока нечего (данные для решения не загрузились или
   *  действие заведомо отклонится) — кнопка неактивна, «Отмена» работает. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
  /** Третий, РАВНОПРАВНЫЙ выбор — не «отмена». Нужен там, где у вопроса два
   *  разных действия («перенести» / «начать отдельный»), а «Отмена» обязана
   *  оставаться отменой: её же вызывают аппаратная «назад» и тап по фону. */
  secondaryLabel?: string;
  onSecondary?: () => void;
}): React.ReactElement {
  useBackGuard(true, onCancel);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-3.5" onClick={(e) => e.stopPropagation()}>
        <div className="text-[16px] font-bold">{title}</div>
        <div className="mt-1 text-[12px] text-slate-600">{body}</div>
        <div className="mt-3 flex gap-2">
          <Button variant="outline" className="h-12 flex-[0.4]" onClick={onCancel} disabled={confirmBusy}>
            Отмена
          </Button>
          {secondaryLabel && onSecondary && (
            <Button variant="outline" className="h-12 flex-1" onClick={onSecondary} disabled={confirmBusy}>
              {secondaryLabel}
            </Button>
          )}
          <Button className={cn("h-12 flex-1", danger ? "bg-red-700 hover:bg-red-800" : "bg-sky-700 hover:bg-sky-800")} onClick={onConfirm} disabled={confirmBusy || confirmDisabled}>
            {confirmBusy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />} {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
